import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, X, ChevronDown, User, UserX, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api/client';

interface Manager {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ManagerSelectorProps {
  value: string;
  onChange: (managerId: string) => void;
  excludeUserId?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const ManagerSelector: React.FC<ManagerSelectorProps> = ({
  value,
  onChange,
  excludeUserId,
  placeholder = "Search for a manager...",
  disabled = false,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Set initial selected manager based on value prop
  useEffect(() => {
    if (value && managers.length > 0) {
      const manager = managers.find(m => m.id === value);
      if (manager) {
        setSelectedManager(manager);
        setSearchQuery(manager.name);
      }
    }
  }, [value, managers]);

  // Load managers when dropdown opens
  useEffect(() => {
    if (isOpen && managers.length === 0) {
      fetchManagers('');
    }
  }, [isOpen]);

  // Search managers when query changes
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const debounceTimer = setTimeout(() => {
        fetchManagers(searchQuery);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else if (searchQuery.length === 0) {
      fetchManagers('');
    }
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  const fetchManagers = async (query: string) => {
    try {
      setLoading(true);
      const results = await apiClient.searchManagers(query);
      // Filter out the excluded user
      const filteredResults = results.filter(manager => manager.id !== excludeUserId);
      setManagers(filteredResults);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (manager: Manager) => {
    setSelectedManager(manager);
    onChange(manager.id);
    setSearchQuery(manager.name);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedManager(null);
    onChange('');
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    
    if (!query) {
      setSelectedManager(null);
      onChange('');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    const roleClasses: Record<string, string> = {
      'ceo': 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30',
      'admin': 'bg-gradient-to-r from-red-500/20 to-rose-500/20 text-red-400 border-red-500/30',
      'ops_head': 'bg-gradient-to-r from-purple-500/20 to-violet-500/20 text-purple-400 border-purple-500/30',
      'team_lead': 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 border-blue-500/30',
      'manager': 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border-green-500/30',
    };
    return roleClasses[role] || 'bg-glass-surface text-text-muted border-glass-border';
  };

  const formatRole = (role: string) => {
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-2">
      <label className="text-xs sm:text-body-sm font-medium text-text-primary flex items-center gap-2">
        <User className="w-4 h-4 text-primary-400" />
        Manager (Optional)
        <span className="text-xs text-text-muted">• Team Lead and above</span>
      </label>
      
      <div className="relative" ref={dropdownRef}>
        {/* Input Field */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
            disabled={disabled}
            className={`
              w-full pl-10 pr-16 py-2.5 
              rounded-xl text-xs sm:text-sm
              backdrop-blur-md border transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50
              ${error
                ? 'border-error-500/50 bg-error-500/10 text-text-primary'
                : `
                  bg-glass-surface dark:bg-glass-surface-dark-mode
                  border-glass-border dark:border-glass-border-dark-mode
                  text-text-primary
                  hover:border-primary-500/30 hover:bg-glass-surface/80 dark:hover:bg-glass-surface-dark-mode/80
                `
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
              placeholder:text-text-muted
            `}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {selectedManager && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 rounded-md text-text-muted hover:text-error-400 hover:bg-error-500/10 transition-colors duration-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <ChevronDown className={`w-4 h-4 text-text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className="
              absolute top-full left-0 right-0 mt-2
              backdrop-blur-xl border rounded-xl shadow-2xl
              z-50 max-h-72 overflow-y-auto overflow-x-hidden
              border-glass-border dark:border-glass-border-dark-mode
              animate-in fade-in-0 zoom-in-95 duration-200
              bg-[linear-gradient(135deg,rgba(248,249,250,0.97)_0%,rgba(241,243,245,0.99)_100%)]
              dark:bg-[linear-gradient(135deg,rgba(28,28,30,0.98)_0%,rgba(18,18,20,0.99)_100%)]
            "
          >
            <div className="relative">
              {/* No Manager Option */}
              <div
                className={`
                  p-3 cursor-pointer transition-all duration-200
                  border-b border-gray-200/50 dark:border-gray-700/50
                  ${!selectedManager 
                    ? 'bg-primary-500/10 dark:bg-primary-500/20' 
                    : 'hover:bg-gray-100/80 dark:hover:bg-gray-800/60'
                  }
                `}
                onClick={() => {
                  setSelectedManager(null);
                  onChange('');
                  setSearchQuery('');
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                      <UserX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">No Manager</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Direct report (no manager assigned)</div>
                    </div>
                  </div>
                  {!selectedManager && (
                    <div className="w-6 h-6 rounded-full bg-success-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-success-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="p-6 flex flex-col items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
                  <span className="text-sm">Loading managers...</span>
                </div>
              ) : managers.length > 0 ? (
                /* Manager List */
                <div className="py-1">
                  {managers.map((manager) => (
                    <div
                      key={manager.id}
                      className={`
                        p-3 cursor-pointer transition-all duration-200
                        ${selectedManager?.id === manager.id 
                          ? 'bg-primary-500/10 dark:bg-primary-500/20' 
                          : 'hover:bg-gray-100/80 dark:hover:bg-gray-800/60'
                        }
                      `}
                      onClick={() => handleSelect(manager)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 border border-primary-500/30 flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary-400">
                              {manager.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                              {manager.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {manager.email}
                            </div>
                          </div>
                          {/* Role Badge */}
                          <span className={`
                            px-2 py-0.5 rounded-md text-xs font-medium border
                            ${getRoleBadgeClass(manager.role)}
                          `}>
                            {formatRole(manager.role)}
                          </span>
                        </div>
                        {/* Selected Check */}
                        {selectedManager?.id === manager.id && (
                          <div className="w-6 h-6 rounded-full bg-success-500/20 flex items-center justify-center ml-2">
                            <Check className="w-4 h-4 text-success-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="p-6 flex flex-col items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                  <Search className="w-6 h-6 opacity-50" />
                  <span className="text-sm">No managers found</span>
                  <span className="text-xs">Try a different search term</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Helper Text */}
      <p className="text-xs text-text-muted flex items-center gap-1">
        <span className="text-primary-400">💡</span>
        Managers must be Team Lead or higher. Recruiters report to Team Leads.
      </p>
      
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-1 text-xs text-error-400">
          <X className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
};
